import videojs from 'video.js';
import './list-cameras.js';
import './switch-button.js';
import './camera.js';
import { checkTouchScreen } from "../../js/helpers/checkTouchScreen";

const Plugin = videojs.getPlugin('plugin');

const defaults = {
	isAvailable: true,
	requestAccess: () => {}
};

/**
 * @typedef {Object} AudioTrack
 * @property {string} type - The type of the track
 * @property {string} url - The URL of the track
 * @property {string} title - The title of the track
 * @property {string} id - The unique identifier of the track
 */

/**
 * @typedef {Object} Broadcast
 * @property {string} playlistUrl - The URL of the playlist
 * @property {boolean} defaultSelected - Whether the broadcast is selected by default or not
 * @property {boolean} isMain - Whether the broadcast is the main one or not
 * @property {string} id - The unique identifier of the broadcast
 * @property {string} title - The title of the broadcast
 * @property {number} startAt - The starting time of the broadcast in unix time
 * @property {number} finishAt - The finishing time of the broadcast in unix time
 * @property {Array<AudioTrack>} audioTracks - The list of alternative audio tracks with same video for the broadcast
 * @property {string} cameraThumbnailUrl - The thumbnail url of the broadcast
 * @property {string} eventThumbnailUrl - The thumbnail url fallback of the broadcast
 */

/**
 * @typedef {Object} Options
 * @property {Broadcast} data
 * @property {boolean} isAvailable
 * @property {number} [breakpointToClose]
 */

class Multicam extends Plugin {
	/**
	 * @param {videojs.Player} player
	 * @param {Options} options
	 */
	constructor(player, options) {
		super(player);
		/**
		 * @type {Options}
		 */
		this.options = videojs.mergeOptions({}, defaults, options);

		this.listCameras = null;
		this.switchButton = null;

		player.addClass('vjs-multicam');

		let activeBroadcastName = Object.keys(options.data)
			.find((key) => (
				options.data[key].defaultSelected
			));
		this.setState({
			activeBroadcast: options.data[activeBroadcastName]
		});

		this.setListeners();
	}

	setListeners() {
		this.player.ready(() => {
			this.addListCameras();
			this.addSwitchButton();
			this.addCameras();
			this.initCloseByClickOutside();
		});

		if (this.options.breakpointToClose) {
			this.player.on('playerresize', () => {
				const isCloseListCameras = this.options.breakpointToClose >= this.player.currentWidth() && this.listCameras.opened();
				if (!isCloseListCameras) {
					return;
				}
				this.close();
			});
		}

		// Добавляет камеры в свёрнутое на мобильных меню "...".
		this.player.on('pluginsetup:settingsGroup', () => {
			this.player.settingsGroup().on('settingsGroup/ready', () => {
				if (this.getCameras().length < 2) {
					return;
				}
				let updateActions = [];
				const { update: updateCamerasInSettingsGroup } = this.player.settingsGroup().addSettingsSection({
					name: {
						text: 'Камеры',
						className: !this.options.isAvailable && ((defaultClassName) => `${defaultClassName}_restricted`),
						onClick: !this.options.isAvailable && (() => {
							this.showRestrictionModalDialog(() => {
								this.options.requestAccess();
							})
						})
					},
					componentName: null,
					id: 'cameras',
					getCurrentValue: () => '',
					items: this.getCameras().map((camera) => ({
						name: camera.options_.broadcast.name,
						checkSelected: () => camera.checkSelected(),
						checkDisabled: () => camera.checkDisabled() || !camera.checkStarted(),
						onClick: () => camera.select(),
						getRegisterForInfoUpdate: () => {
							const updateInfoListeners = [];
							const handleUpdate = ({ data }) => {
								updateInfoListeners.forEach((listener) => listener(data));
							};
							camera.on('MulticamCamera/updateRemainingTime', handleUpdate);
							updateActions.push(() => {
								camera.off('MulticamCamera/updateRemainingTime', handleUpdate);
							});
							return (listener) => {
								updateInfoListeners.push(listener);
							};
						}
					}))
				});
				this.player.on(['MulticamCamera/select', 'MulticamCamera/enabled', 'MulticamCamera/disabled'], () => {
					requestAnimationFrame(() => {
						requestAnimationFrame(() => {
							updateActions.forEach((action) => action());
							updateActions = [];
							// requestAnimationFrame внутри requestAnimationFrame
							// нужен чтобы input связанный с камерой сменил своё
							// состояние, а это произойдёт только после рендера
							// который не состоится пока не отработает JS.
							updateCamerasInSettingsGroup();
						});
					});
				});
			});
		});
	}

	initCloseByClickOutside() {
		// Закрытие кликом вне контейнера
		const handleClick = (event) => {
			const ignoreElement = event.target.closest('.vjs-multicam__list-cameras, .vjs-multicam__switch-button');
			if (this.player.currentWidth() >= 1024 || ignoreElement) {
				return;
			}
			this.close();
		};
		const eventName = checkTouchScreen() ? 'touchstart' : 'click';
		document.addEventListener(eventName, handleClick);
		this.player.on('dispose', () => {
			document.removeEventListener(eventName, handleClick);
		});
	}

	addListCameras() {
		this.listCameras = this.player.addChild('ListCameras', {
			onOpen: () => {
				const now = Date.now();
				this.getCameras()
					.forEach((camera) => {
						camera.updateCameraThumbnail(now);
						camera.setVisibility(true);
					});
			},
			onClose: () => {
				this.getCameras()
					.forEach((camera) => {
						camera.setVisibility(false);
					});
				this.player.removeClass('vjs-multicam_show');
			}
		});
	}

	addSwitchButton() {
		const isAvailable = this.options.isAvailable;
		const controlBar = this.player.getChild('controlBar');
		if (!controlBar) return;
		this.switchButton = controlBar.addChild('MulticamSwitchButton', {
			clickHandler: () => {
				if (!isAvailable) {
					this.showRestrictionModalDialog(() => {
						this.options.requestAccess();
					});
					return;
				}
				const restrictionModalDialog = this.player.getChild('restrictionModalDialog');
				if (restrictionModalDialog) {
					restrictionModalDialog.hide();
				}
				if (this.listCameras.opened()) {
					this.close();
				} else {
					this.open();
					this.player.trigger('MulticamSwitchButton/click');
				}
			},
			iconType: isAvailable ? 'enabled' : 'disabled',
			controlText: 'Камеры',
			className: 'vjs-multicam__switch-button'
		});

		if (!isAvailable) {
			this.switchButton.addClass('vjs-multicam__switch-button_restricted');
		}

		if (this.listCameras && this.listCameras.opened()) {
			this.switchButton.turnOn();
		}
	}

	showRestrictionModalDialog(getAccessCallback) {
		const restrictionModalDialog = this.player.getChild('restrictionModalDialog');
		if (!restrictionModalDialog) return;
		restrictionModalDialog.toggleDisplay();
		restrictionModalDialog.onGetAccess(() => getAccessCallback());
	}

	close() {
		this.listCameras.close();
		this.switchButton.turnOff();
	}

	open() {
		this.listCameras.open();
		this.player.addClass('vjs-multicam_show');
		this.switchButton.turnOn();
	}

	addCameras() {
		const now = Date.now();
		Object.keys(this.options.data)
			.forEach((title) => {
				const { isMain } = this.options.data[title];
				const camera = this.listCameras.addChild('MulticamCamera', {
					broadcast: videojs.mergeOptions(
						{ title },
						this.options.data[title]
					),
					handleClick: () => {
						// Если у выбранной камеры есть аудиотреки, то показывается кнопка в контролбаре,
						// а если нет, то она скрывается.
						const audiotracksButton = this.player.getDescendant(['controlBar', 'AudiotracksButton']);
						if (isMain && audiotracksButton) {
							audiotracksButton.show();
						} else if (audiotracksButton) {
							audiotracksButton.hide();
						}
					},
					handleNotStartedBroadcast: () => {
						if (isMain) return;
						const mainCamera = this.getCameras().filter((camera) => camera.checkMain())[0];
						mainCamera.select();
					}
				});
				camera.updateCameraThumbnail(now);
			});
	}

	changeCameraData(data) {
		let camera = this.getCameras()
			.filter((camera) => (
				camera.getBroadcastId() === data.event_id
			))[0];
		if (!camera) return;
		camera.changeData(data);
	}

	getCameras() {
		if (!this.listCameras) return [];
		return this.listCameras.children()
			.filter((component) => (
				component.name() === 'MulticamCamera'
			));
	}

	resetSavedPlayerState() {
		this.setState({
			playerDataBeforeChangeSrc: Multicam.defaultState.playerDataBeforeChangeSrc
		});
	}
}

Multicam.defaultState = {
	activeBroadcast: null,
	playerDataBeforeChangeSrc: {
		currentTime: null,
		currentType: null,
		volume: null,
		paused: null
	}
};

// Cross-compatibility for Video.js 5 and 6.
const registerPlugin = videojs.registerPlugin || videojs.plugin;
registerPlugin('multicam', Multicam);
